import intl from 'react-intl-universal';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Option, UserOption, Task } from 'utils/customTypes';
import { TASK_FIELDS, LONG_INPUTS_LENGTH } from 'utils/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Datepicker,
  Dropdown,
  FormItem,
  NumericInput,
  TextArea,
  TextField,
  TimeInput,
  timeStringToDecimalHours,
  Typography,
  UsersPicker,
} from '@getsynapse/design-system';
import { selectAllUsersForDropdown } from 'state/UsersManagement/usersManagementSlice';
import { getAvailableUsersForTaskAssignees } from 'state/SingleTask/singleTaskSlice';
import TypePicker from 'Molecules/TypePicker/TypePicker';
import { TaskFormFields } from 'utils/types/task';
import { difference, getInitialValueForDropDown } from 'utils/functions';
import ProjectPicker from './ProjectPicker';
import { canCreateOrUpdateTask } from './canCreateOrUpdateTask';
import {
  fetchTaskTypes,
  selectCanFetchTaskTypes,
  selectTaskTypes,
  selectTaskTypesFetchedWithV3,
  selectTaskTypesLoadingStatus,
} from 'state/TasksList/allTasks/allTasksSlice';
import { useAppThunkDispatch } from 'state/store';

interface TaskFormProps {
  canCreateTask: boolean;
  isTaskTemplate: boolean;
  isCentralizedTask: boolean;
  taskData: Task;
  projectId: string;
  setTaskFields: React.Dispatch<React.SetStateAction<TaskFormFields>>;
  setCanCreateTask: React.Dispatch<React.SetStateAction<boolean>>;
  onChangeValues?: (hasChanges: boolean) => void;
  extraSection?: React.ReactNode;
  extraHasChanges?: boolean;
}

const TaskForm: React.FC<TaskFormProps> = ({
  canCreateTask,
  isTaskTemplate,
  isCentralizedTask,
  taskData,
  projectId,
  setCanCreateTask,
  setTaskFields = () => {},
  onChangeValues = () => {},
  extraSection,
  extraHasChanges = false,
}) => {
  const {
    tasksAssignmentsBasedResourcesCapacity = false,
    taskAssigneeDateRestriction = true,
    flexibleTaskTimeEntry,
    newTaskTypes = false,
  } = useFlags();
  const availableUsers = useSelector(
    tasksAssignmentsBasedResourcesCapacity
      ? selectAllUsersForDropdown
      : getAvailableUsersForTaskAssignees
  );

  const [fieldsValues, setFieldsValues] = useState<TaskFormFields>({
    name: taskData?.name,
    type: taskData?.type,
    description: taskData?.description,
    start_date: '',
    due_date: '',
    estimate_hours: !!taskData?.estimate_hours ? taskData.estimate_hours : '',
    project_id: projectId,
    assignedUserIds: [],
  });

  useEffect(() => {
    setFieldsValues((prevData) => ({
      ...prevData,
      name: taskData?.name,
      type: taskData.type,
      description: taskData.description,
      estimate_hours: !!taskData?.estimate_hours ? taskData.estimate_hours : '',
    }));
  }, [setFieldsValues, taskData]);

  const updateValue = (newValue: any) => {
    setFieldsValues((prevData) => ({ ...prevData, ...newValue }));
  };

  const hasAssingees = fieldsValues.assignedUserIds?.length > 0;

  const handlePickOwners = (owners: UserOption[]) => {
    const ownersIds = owners.map((owner) => owner.value);
    updateValue({ [TASK_FIELDS.ASSIGNEE_ADD]: ownersIds });
  };

  const hasAddedRequiredValues = useMemo<boolean>(
    () =>
      canCreateOrUpdateTask({
        fieldsValues,
        hasAssignees: hasAssingees,
        taskAssigneeDateRestriction,
        isTaskTemplate,
      }),
    [fieldsValues, hasAssingees, taskAssigneeDateRestriction, isTaskTemplate]
  );

  const hasUpdatedValues = useMemo<boolean>(() => {
    const assigneesDiff: string[] = difference(
      taskData.assignedUsers?.map((user) => user.id) || [],
      fieldsValues.assignedUserIds
    );

    return Boolean(
      fieldsValues.name !== taskData.name ||
        fieldsValues.type !== taskData.type ||
        fieldsValues.description !== taskData.description ||
        fieldsValues.start_date !== taskData.start_date ||
        fieldsValues.due_date !== taskData.due_date ||
        fieldsValues.estimate_hours !== taskData.estimate_hours ||
        assigneesDiff.length > 0
    );
  }, [
    fieldsValues.assignedUserIds,
    fieldsValues.description,
    fieldsValues.due_date,
    fieldsValues.estimate_hours,
    fieldsValues.name,
    fieldsValues.start_date,
    fieldsValues.type,
    taskData.assignedUsers,
    taskData.description,
    taskData.due_date,
    taskData.estimate_hours,
    taskData.name,
    taskData.start_date,
    taskData.type,
  ]);

  useEffect(() => {
    if (hasAddedRequiredValues && (hasUpdatedValues || extraHasChanges)) {
      !canCreateTask && setCanCreateTask(true);
    } else {
      canCreateTask && setCanCreateTask(false);
    }
  }, [
    canCreateTask,
    extraHasChanges,
    hasAddedRequiredValues,
    hasUpdatedValues,
    setCanCreateTask,
  ]);

  const dispatch = useAppThunkDispatch();
  const canFetchTaskTypes = useSelector(selectCanFetchTaskTypes);
  const taskTypesFetchedWithV3 = useSelector(selectTaskTypesFetchedWithV3);
  const taskTypesLoading = useSelector(selectTaskTypesLoadingStatus);
  const taskTypes = useSelector(selectTaskTypes);

  const init = useCallback(() => {
    const versionMismatch =
      !taskTypesLoading && taskTypesFetchedWithV3 !== newTaskTypes;
    if (canFetchTaskTypes || versionMismatch) {
      dispatch(fetchTaskTypes(newTaskTypes));
    }
  }, [
    canFetchTaskTypes,
    dispatch,
    newTaskTypes,
    taskTypesFetchedWithV3,
    taskTypesLoading,
  ]);

  useEffect(() => {
    init();
    return () => {};
  }, [init]);

  useEffect(() => {
    onChangeValues(
      hasUpdatedValues || hasAddedRequiredValues || extraHasChanges
    );
  }, [
    hasUpdatedValues,
    hasAddedRequiredValues,
    extraHasChanges,
    onChangeValues,
  ]);

  useEffect(() => {
    setTaskFields(fieldsValues);
  }, [setTaskFields, fieldsValues]);

  const formPrompt = useMemo<string>(() => {
    const container: string = isTaskTemplate
      ? intl.get('ENTITIES.TASK_BUNDLE', { num: 1 }).toLowerCase()
      : intl.get('ENTITIES.PROJECT', { num: 1 }).toLowerCase();

    return isTaskTemplate && taskData.id
      ? intl.get('TASK_MODAL.UPDATE_PROMPT', { container })
      : intl.get('TASK_MODAL.ADD_PROMPT', { container });
  }, [isTaskTemplate, taskData.id]);

  return (
    <div className='flex flex-col'>
      <Typography>{formPrompt}</Typography>

      <div className='mt-4 flex w-full gap-x-10 px-px'>
        <div className='grid gap-y-4 w-2/4'>
          <FormItem>
            <TextField
              labelProps={{ required: true }}
              label={intl.get('TASKS.ADD_TASK_MODAL.TASK_TITLE')}
              displayCharactersCount
              maxLength={LONG_INPUTS_LENGTH}
              defaultValue={fieldsValues.name}
              onChange={(e: any) =>
                updateValue({ [TASK_FIELDS.NAME]: e.target.value })
              }
              placeholder={intl.get(
                'TASKS.ADD_TASK_MODAL.TASK_TITLE_PLACEHOLDER'
              )}
              className='w-full'
              data-cy='task_title-name'
              data-testid='task_title-name'
            />
          </FormItem>

          {newTaskTypes ? (
            <FormItem label={intl.get('TASKS.ADD_TASK_MODAL.TASK_TYPE')}>
              <Dropdown
                onChange={(option: Option) =>
                  updateValue({
                    [TASK_FIELDS.TASK_TYPE]: option.value.toLowerCase(),
                  })
                }
                options={Object.entries(taskTypes).map(([code, name]) => ({
                  value: code.toUpperCase(),
                  label: name,
                }))}
                values={getInitialValueForDropDown(
                  Object.entries(taskTypes).map(([code, name]) => ({
                    value: code.toUpperCase(),
                    label: name,
                  })),
                  [
                    (fieldsValues.type || taskData.type || '').toUpperCase(),
                  ].filter(Boolean)
                )}
                triggerProps={{
                  'data-cy': 'task-type',
                  'data-testid': 'task-type',
                  placeholder: intl.get(
                    'TASKS.ADD_TASK_MODAL.TASK_TYPE_PLACEHOLDER'
                  ),
                }}
              />
            </FormItem>
          ) : (
            <TypePicker
              formItemProps={{
                label: intl.get('TASKS.ADD_TASK_MODAL.TASK_TYPE'),
              }}
              dropdownProps={{
                onChange: (option: Option) =>
                  updateValue({
                    [TASK_FIELDS.TASK_TYPE]: option.value.toLowerCase(),
                  }),
                triggerProps: {
                  'data-cy': 'task-type',
                  'data-testid': 'task-type',
                  placeholder: intl.get(
                    'TASKS.ADD_TASK_MODAL.TASK_TYPE_PLACEHOLDER'
                  ),
                },
              }}
              currentValue={(
                fieldsValues.type ||
                taskData.type ||
                ''
              ).toUpperCase()}
              options={Object.keys(taskTypes).map((k) => k.toUpperCase())}
              labelRoute='TASKS.TASK_TYPES'
            />
          )}

          {!isTaskTemplate && (
            <FormItem>
              <Datepicker
                className='flex justify-evenly w-full'
                canSelectRange={true}
                startDateLabel={intl.get('TASKS.ADD_TASK_MODAL.START_DATE')}
                size='large'
                onPickDate={(dates: any) =>
                  updateValue({
                    [TASK_FIELDS.START_DATE]: dates.startDate || '',
                    [TASK_FIELDS.DUE_DATE]: dates.endDate || '',
                  })
                }
                endDateLabel={intl.get('TASKS.ADD_TASK_MODAL.END_DATE')}
                data-cy='task-input_date'
                startDateLabelProps={{
                  required: hasAssingees && taskAssigneeDateRestriction,
                }}
                endDateLabelProps={{
                  required: hasAssingees && taskAssigneeDateRestriction,
                }}
              />
            </FormItem>
          )}

          <FormItem
            component='div'
            label={intl.get('TASK.ESTIMATED_HOURS')}
            className='pb-px'
          >
            <div className='flex items-center'>
              {flexibleTaskTimeEntry ? (
                <TimeInput
                  value={fieldsValues.estimate_hours?.toString() ?? ''}
                  data-cy='estimated_time'
                  data-testid='estimated_time'
                  placeholder={intl.get(
                    'TASKS.ADD_TASK_MODAL.ESTIMATED_TIME_PLACEHOLDER'
                  )}
                  divProps={{ className: 'w-full' }}
                  onChange={(value: string) => {
                    const decimalHours = timeStringToDecimalHours(value);
                    if (decimalHours != null) {
                      updateValue({
                        [TASK_FIELDS.ESTIMATED_HOURS]: decimalHours.toString(),
                      });
                    } else if (value === '') {
                      updateValue({
                        [TASK_FIELDS.ESTIMATED_HOURS]: '',
                      });
                    }
                  }}
                />
              ) : (
                <>
                  <NumericInput
                    data-cy='estimated_time'
                    data-testid='estimated_time'
                    placeholder={intl.get(
                      'TASKS.ADD_TASK_MODAL.ESTIMATED_TIME_PLACEHOLDER'
                    )}
                    containerClassName='w-11/12'
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      updateValue({
                        [TASK_FIELDS.ESTIMATED_HOURS]: event.target.value,
                      });
                    }}
                    defaultValue={parseFloat(fieldsValues.estimate_hours)}
                  />
                  <span className='pl-4'>hrs</span>
                </>
              )}
            </div>
          </FormItem>
        </div>

        <div className='grid gap-y-4 w-2/4'>
          {isCentralizedTask && (
            <ProjectPicker
              dropdownProps={{
                onChange: (option: Option) =>
                  updateValue({ project_id: option.value }),
              }}
            />
          )}

          <FormItem label={intl.get('TASKS.ADD_TASK_MODAL.TASK_DESCRIPTION')}>
            <TextArea
              textAreaProps={{
                className: 'max-h-30',
                placeholder: intl.get(
                  'TASKS.ADD_TASK_MODAL.TASK_DESCRIPTION_PLACEHOLDER'
                ),
                'data-cy': 'task_description',
                'data-testid': 'task_description',
                defaultValue: fieldsValues.description,
              }}
              onChange={(e) =>
                updateValue({ [TASK_FIELDS.DESCRIPTION]: e.target.value })
              }
            />
          </FormItem>

          {!isTaskTemplate && (
            <FormItem
              label={intl.get('TASKS.ADD_TASK_MODAL.TASK_ASSIGNEE')}
              className='text-primary-light font-medium'
            >
              <UsersPicker
                deactivatedUserText={intl.get('DEACTIVATED')}
                triggerText={intl.get(
                  'TASKS.ADD_TASK_MODAL.TASK_ASSIGNEE_PLACEHOLDER'
                )}
                usersList={availableUsers}
                triggerProps={{
                  'data-cy': 'user_picker',
                }}
                onChange={handlePickOwners}
              />
            </FormItem>
          )}
        </div>
      </div>
      {extraSection && <div className='mt-6'>{extraSection}</div>}
    </div>
  );
};

export default TaskForm;
